// ProductFiltersDrawer.jsx
import React, {useEffect, useRef} from 'react'
import PropTypes from 'prop-types'
import {
    Drawer,
    DrawerOverlay,
    DrawerContent,
    DrawerHeader,
    DrawerBody,
    DrawerFooter,
    Heading,
    Divider,
    Box,
    Text,
    Button,
    Stack,
    Image,
    useBreakpointValue
} from '@chakra-ui/react'
import {FormattedMessage, useIntl} from 'react-intl'
import DrawerRefinements from './drawer-refinements'
import LoadingSpinner from '../../../components/loading-spinner'
import {getUrl} from '../../../utils/cloudinary-utils'
import Sort from './product-sorts'
import {HIDE_FILTERS} from '../../../constants'

const FILTER_DRAWER_SPINNER_Z_INDEX = '10'

const ProductFiltersDrawer = ({
    isOpen,
    onClose,
    inStockAt,
    filters,
    filtersLoading,
    toggleFilter,
    resetFilters,
    productSearchResult,
    brResponse,
    sortUrls,
    brSortingOptions,
    tagManagerProductFilters,
    searchParams,
    defaultSortingOption,
    basePath,
    productListStyles
}) => {
    const intl = useIntl()
    const isMobile = useBreakpointValue({base: true, md: false})
    const handleClose = filtersLoading ? undefined : onClose
    const hasDocumentScrollLock = useRef(false)
    const resultCount = brResponse?.numFound ?? productSearchResult?.total

    useEffect(() => {
        if (isOpen) {
            document.querySelector('html').style.overflow = 'hidden'
            document.querySelector('html').classList.add('block-scroll')
            hasDocumentScrollLock.current = true
            return
        }

        // Restoring document scroll can force a full-page style/layout pass. Keep it
        // outside the drawer-close presentation window.
        let secondAnimationFrame
        const firstAnimationFrame = window.requestAnimationFrame(() => {
            secondAnimationFrame = window.requestAnimationFrame(() => {
                if (hasDocumentScrollLock.current) {
                    document.querySelector('html').style.overflow = ''
                    document.querySelector('html').classList.remove('block-scroll')
                    hasDocumentScrollLock.current = false
                }
            })
        })

        return () => {
            window.cancelAnimationFrame(firstAnimationFrame)
            if (secondAnimationFrame) {
                window.cancelAnimationFrame(secondAnimationFrame)
            }
        }
    }, [isOpen])

    useEffect(() => {
        return () => {
            if (hasDocumentScrollLock.current) {
                document.querySelector('html').style.overflow = ''
                document.querySelector('html').classList.remove('block-scroll')
                hasDocumentScrollLock.current = false
            }
        }
    }, [])

    return (
        <Drawer
            placement={isMobile ? 'bottom' : 'right'}
            isOpen={isOpen}
            onClose={handleClose || (() => {})}
            size="md"
            motionPreset={{base: 'slideInBottom', md: 'slideInRight'}}
            scrollBehavior="inside"
            blockScrollOnMount={false}
            isFullHeight={false}
            returnFocusOnClose={false}
        >
            <DrawerOverlay background="blackAlpha600" />
            <DrawerContent marginTop={0} {...productListStyles?.body?.refinementsModal?.content}>
                {filtersLoading && (
                    <LoadingSpinner
                        position="absolute"
                        top="0"
                        height="100%"
                        zIndex={FILTER_DRAWER_SPINNER_Z_INDEX}
                        pointerEvents="auto"
                        displayOverlay="none"
                    />
                )}
                {/* Header */}
                <DrawerHeader
                    boxShadow="none"
                    margin={{base: '20px 20px 0px 20px', lg: '20px 20px 11px 20px'}}
                    padding="0 !important"
                >
                    <Heading
                        as="h1"
                        fontWeight="400"
                        marginBottom="20px"
                        color="black.100"
                        fontSize="29px"
                        lineHeight="normal"
                        display={{base: 'none', md: 'block'}}
                    >
                        <FormattedMessage
                            defaultMessage="Filter & Sort"
                            id="product_list.modal.title.filter.and.sort"
                        />
                    </Heading>
                    <Heading
                        as="h1"
                        fontWeight="400"
                        color="black.100"
                        fontSize="27px"
                        lineHeight="normal"
                        display={{base: 'block', md: 'none'}}
                    >
                        <FormattedMessage
                            defaultMessage="Filter"
                            id="product_list.modal.title.filter"
                        />
                    </Heading>
                    <Divider
                        color="gray.500"
                        margin="20px 0"
                        border="1px solid"
                        display={{base: 'block', md: 'none'}}
                    />

                    {typeof resultCount === 'number' && (
                        <Box
                            className="selected-filters-count"
                            gap="10px"
                            marginBottom="20px"
                            display={{base: 'none', md: 'flex'}}
                        >
                            <Text
                                width="fit-content"
                                fontWeight="300"
                                color="black.100"
                                fontSize="16px"
                            >
                                <FormattedMessage
                                    defaultMessage="{count} item(s)"
                                    values={{
                                        count: resultCount
                                    }}
                                    id="selected.filters.count"
                                />
                            </Text>
                            <Button
                                width="fit-content"
                                aria-label={intl.formatMessage({
                                    id: 'product_list.modal.button.reset_filters',
                                    defaultMessage: 'Reset Filters'
                                })}
                                fontSize="16px"
                                fontWeight="300"
                                variant="link"
                                textUnderlineOffset="4px"
                                onClick={filtersLoading ? undefined : resetFilters}
                                textDecoration="underline"
                                isDisabled={filtersLoading}
                            >
                                <FormattedMessage
                                    defaultMessage="Reset Filters"
                                    id="product_list.modal.button.reset_filters"
                                />
                            </Button>
                        </Box>
                    )}

                    <Box display={{base: 'none', md: 'block'}}>
                        <Sort
                            sortUrls={sortUrls}
                            brSortingOptions={brSortingOptions}
                            productSearchResult={productSearchResult}
                            tagManagerProductFilters={tagManagerProductFilters}
                            isMenu={true}
                            searchParams={searchParams}
                            defaultSortingOption={defaultSortingOption}
                            sortSection="drawer"
                            basePath={basePath}
                        />
                    </Box>
                </DrawerHeader>

                {/* Close Button */}
                <Box
                    position="absolute"
                    top="10px"
                    right="10px"
                    padding="15px"
                    borderRadius="50%"
                    onClick={handleClose}
                    _hover={{backgroundColor: 'gray.100'}}
                    transition="all, 0.3s ease"
                    cursor="pointer"
                >
                    <Image
                        width="15px"
                        height="15px"
                        src={getUrl('UIImages/images/crossed-icon.svg')}
                        alt="Close"
                    />
                </Box>

                {/* Body */}
                <DrawerBody
                    padding={{base: '0 20px 20px 20px', md: '30px 20px'}}
                    className="modal-filters"
                >
                    <DrawerRefinements
                        itemsBefore={[
                            <Box
                                key="mobile-drawer-sort"
                                display={{base: 'block', md: 'none'}}
                                margin="0 0 15px"
                            >
                                <Sort
                                    sortUrls={sortUrls}
                                    brSortingOptions={brSortingOptions}
                                    productSearchResult={productSearchResult}
                                    tagManagerProductFilters={tagManagerProductFilters}
                                    isMenu={true}
                                    searchParams={searchParams}
                                    defaultSortingOption={defaultSortingOption}
                                    sortSection="mobile-drawer"
                                    basePath={basePath}
                                />
                            </Box>
                        ]}
                        toggleFilter={toggleFilter}
                        isMenu={true}
                        inStockAt={inStockAt}
                        filters={filters}
                        selectedFilters={searchParams?.refine}
                        excludedFilters={HIDE_FILTERS}
                    />
                </DrawerBody>

                {/* Footer */}
                <DrawerFooter {...productListStyles?.modal?.footer} bottom="65px">
                    <Stack>
                        <Button
                            width="full"
                            onClick={handleClose}
                            borderRadius="90px"
                            color="white"
                            backgroundColor="black.100"
                            fontSize="17px"
                            aria-label="View Items"
                            height="56px"
                            _hover={{backgroundColor: 'black.100'}}
                        >
                            {intl.formatMessage(
                                {
                                    id: 'product_list.modal.button.view_items',
                                    defaultMessage: 'View {prroductCount} items'
                                },
                                {
                                    prroductCount: resultCount ?? 0
                                }
                            )}
                        </Button>
                        <Button
                            width="full"
                            variant="link"
                            onClick={filtersLoading ? undefined : resetFilters}
                            aria-label={intl.formatMessage({
                                id: 'product_list.modal.button.reset_filters',
                                defaultMessage: 'Reset Filters'
                            })}
                            isDisabled={filtersLoading}
                            color="black.100"
                            textDecoration="underline"
                            textUnderlineOffset="4px"
                            margin="10px 0"
                        >
                            <FormattedMessage
                                defaultMessage="Reset Filters"
                                id="product_list.modal.button.reset_filters"
                            />
                        </Button>
                    </Stack>
                </DrawerFooter>
            </DrawerContent>
        </Drawer>
    )
}

ProductFiltersDrawer.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    filters: PropTypes.arrayOf(PropTypes.object),
    filtersLoading: PropTypes.bool,
    toggleFilter: PropTypes.func,
    inStockAt: PropTypes.any,
    resetFilters: PropTypes.func,
    productSearchResult: PropTypes.shape({
        total: PropTypes.number,
        sortingOptions: PropTypes.arrayOf(
            PropTypes.shape({
                label: PropTypes.string
            })
        )
    }),
    brResponse: PropTypes.shape({
        numFound: PropTypes.number
    }),
    sortUrls: PropTypes.arrayOf(PropTypes.string),
    brSortingOptions: PropTypes.arrayOf(
        PropTypes.shape({
            label: PropTypes.string
        })
    ),
    tagManagerProductFilters: PropTypes.func,
    searchParams: PropTypes.shape({
        refine: PropTypes.object,
        sort: PropTypes.string
    }),
    defaultSortingOption: PropTypes.string,
    basePath: PropTypes.string,
    productListStyles: PropTypes.object
}

export default ProductFiltersDrawer
